import { useEffect, useState, useMemo } from "react";
import { GlassPanel } from "@/components/ui/glass-panel";
import { Compass, Sparkle } from "lucide-react";
import { cn } from "@/lib/utils";
import { planetPositions } from "@/lib/astronomy";
import { useLocation } from "@/components/location-provider";

const PLANET_COLORS: Record<string, string> = {
  Mercury: "text-amber-300",
  Venus: "text-yellow-100",
  Mars: "text-rose-400",
  Jupiter: "text-orange-200",
  Saturn: "text-amber-200",
};

const PLANET_EMOJI: Record<string, string> = {
  Mercury: "☿",
  Venus: "♀",
  Mars: "♂",
  Jupiter: "♃",
  Saturn: "♄",
};

function compassLabel(az: number): string {
  const dirs = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
  return dirs[Math.round(az / 45) % 8];
}

export function PlanetsTonight() {
  const [now, setNow] = useState(() => new Date());
  const { current: location } = useLocation();

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 5 * 60_000);
    return () => clearInterval(t);
  }, []);

  // For each planet, compute current altitude AND scan the next 24 hours to
  // find when (if at all) it's above the horizon.
  const planets = useMemo(() => {
    const current = planetPositions(now, location.lat, location.lon);
    const result = current.map((p) => {
      // Find rise/set within next 24h
      let riseTs: number | null = null;
      let setTs: number | null = null;
      let prevAlt = p.altitudeDeg;
      for (let h = 0.5; h <= 24; h += 0.5) {
        const t = new Date(now.getTime() + h * 3600_000);
        const pp = planetPositions(t, location.lat, location.lon).find((x) => x.name === p.name)!;
        if (prevAlt < 0 && pp.altitudeDeg >= 0 && riseTs == null) {
          riseTs = t.getTime() / 1000;
        }
        if (prevAlt > 0 && pp.altitudeDeg <= 0 && setTs == null) {
          setTs = t.getTime() / 1000;
        }
        prevAlt = pp.altitudeDeg;
      }
      return { ...p, riseTs, setTs };
    });
    return result;
  }, [now, location.lat, location.lon]);

  return (
    <GlassPanel intensity="low" className="p-4 sm:p-5">
      <div className="flex items-center gap-2.5 mb-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-orange-500/10 ring-1 ring-orange-500/20">
          <Compass className="h-4 w-4 text-orange-300" />
        </div>
        <div>
          <h3 className="text-sm font-bold tracking-tight">Visible Planets</h3>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Position now · approx magnitude
          </p>
        </div>
      </div>

      <div className="space-y-1.5">
        {planets.map((p) => {
          const visible = p.altitudeDeg > 0;
          const color = PLANET_COLORS[p.name];
          return (
            <div
              key={p.name}
              className={cn(
                "rounded-lg border px-3 py-2 flex items-center gap-3",
                visible
                  ? "border-emerald-500/20 bg-emerald-500/5"
                  : "border-border/40 bg-background/30",
              )}
            >
              <span className={cn("text-2xl flex-shrink-0 leading-none", color)} aria-hidden>
                {PLANET_EMOJI[p.name]}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2">
                  <p className="text-sm font-bold">{p.name}</p>
                  <p className="text-[10px] text-muted-foreground tabular-nums">
                    mag {p.magnitudeApprox.toFixed(1)} · {Math.round(p.illuminatedFraction * 100)}% lit
                  </p>
                </div>
                <p className="text-[11px] text-muted-foreground leading-tight">
                  {visible ? (
                    <>
                      Above horizon · alt {p.altitudeDeg.toFixed(0)}° ·{" "}
                      {compassLabel(p.azimuthDeg)} (az {p.azimuthDeg.toFixed(0)}°)
                    </>
                  ) : (
                    <>
                      Below horizon
                      {p.riseTs && (
                        <>
                          {" "}· rises{" "}
                          <span className="text-foreground/80 font-medium tabular-nums">
                            {new Date(p.riseTs * 1000).toLocaleTimeString([], {
                              hour: "numeric",
                              minute: "2-digit",
                            })}
                          </span>
                        </>
                      )}
                    </>
                  )}
                </p>
              </div>
              <span
                className={cn(
                  "text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border flex-shrink-0",
                  visible
                    ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
                    : "border-border/50 text-muted-foreground",
                )}
              >
                {visible ? "Up" : "Down"}
              </span>
            </div>
          );
        })}
      </div>

      <p className="mt-3 text-[10px] text-muted-foreground inline-flex items-center gap-1.5">
        <Sparkle className="h-2.5 w-2.5 text-primary" />
        Magnitudes brighter than +6 are visible to the naked eye in dark sky.
      </p>
    </GlassPanel>
  );
}
